import * as React from 'react'

import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual'
import { classList } from 'react-classlist-helper'

interface IProps<T> {
  items: T[]
  itemHeight: number | ((index: number) => number)
  renderItem: (item: T, virtualItem: VirtualItem) => React.ReactNode

  containerHeight?: number
  containerClassName?: string
  itemsContainerClassName?: string
  overscan?: number
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void
  noItemsRenderer?: () => React.ReactNode
}

const DEFAULT_CONTAINER_HEIGHT = 100
const DEFAULT_OVERSCAN = 10

export default function VirtualizedList<T>(props: IProps<T>) {
  const {
    items,
    itemHeight,
    renderItem,
    containerHeight = DEFAULT_CONTAINER_HEIGHT,
    containerClassName,
    itemsContainerClassName,
    overscan = DEFAULT_OVERSCAN,
    onScroll,
    noItemsRenderer,
  } = props

  const ref = React.useRef<HTMLDivElement>(null)

  const estimateSize = React.useCallback(
    (index: number) => {
      return typeof itemHeight === 'function' ? itemHeight(index) : itemHeight
    },
    [itemHeight],
  )

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => ref.current,
    estimateSize,
    overscan,
  })

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()

  if (!items.length && noItemsRenderer) {
    return <>{noItemsRenderer()}</>
  }

  return (
    <div
      ref={ref}
      className={classList({
        'overflow-auto': true,
        [containerClassName]: !!containerClassName,
      })}
      style={{ height: containerHeight }}
      onScroll={onScroll}
    >
      <div
        className={classList({
          'relative full-width': true,
          [itemsContainerClassName]: !!itemsContainerClassName,
        })}
        style={{ height: `${totalSize}px` }}
      >
        {virtualItems.map(virtualItem => (
          <div
            key={virtualItem.index}
            className="absolute-top left0 full-width"
            style={{
              height: `${virtualItem.size}px`,
              transform: `translateY(${virtualItem.start}px)`,
            }}
          >
            {renderItem(items[virtualItem.index], virtualItem)}
          </div>
        ))}
      </div>
    </div>
  )
}
